import {
  FILAMENT_COLORS,
  Filament,
  FilamentList,
  MATERIAL_DEFAULTS,
  Material,
  OkResponse,
  type CreateFilamentBody,
  formatBRL,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { AffixInput, Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Drawer, Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert, EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import {
  formatG,
  useFilamentPrices,
  useInventorySummary,
  useSpools,
} from '../../lib/inventory-api';
import { centavosInput, decimalInput, parseCentavos, parseDecimal } from '../../lib/numbers';
import { ShoppingTab } from './ShoppingTab';
import { GramsBar, SpoolBadge } from './spool-common';
import { SpoolsTab } from './SpoolsTab';
import { ExportCsvButton } from '../../components/ExportCsv';

const key = ['filaments'] as const;

interface Form {
  brand: string;
  material: Material;
  name: string;
  colorName: string;
  colorHex: string;
  price: string;
  minimum: string;
  diameter: string;
  density: string;
  netWeight: string;
  emptySpool: string;
  nozzle: string;
  bed: string;
  notes: string;
  links: string;
}

function toForm(f?: Filament): Form {
  const d = MATERIAL_DEFAULTS[f?.material ?? 'PLA'];
  return {
    brand: f?.brand ?? 'Bambu Lab',
    material: f?.material ?? 'PLA',
    name: f?.name ?? '',
    colorName: f?.colorName ?? 'Coral',
    colorHex: f?.colorHex ?? '#FF5C5C',
    price: centavosInput(f?.pricePerKgCentavos ?? 11_990),
    minimum: f?.minimumGrams != null ? String(f.minimumGrams) : '500',
    diameter: decimalInput(f?.diameterMm ?? 1.75, 2, 2),
    density: decimalInput(f?.density ?? d.density, 2, 2),
    netWeight: String(f?.netWeightGrams ?? 1000),
    emptySpool: f?.emptySpoolGrams != null ? String(f.emptySpoolGrams) : '',
    nozzle: f?.nozzleTempC != null ? String(f.nozzleTempC) : String(d.nozzleTempC),
    bed: f?.bedTempC != null ? String(f.bedTempC) : String(d.bedTempC),
    notes: f?.printNotes ?? '',
    links: (f?.supplierLinks ?? []).join('\n'),
  };
}

const sameHex = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

const int = (t: string) => (/^\d+$/.test(t.trim()) ? Number.parseInt(t, 10) : null);

function toBody(f: Form, full: boolean): CreateFilamentBody {
  const base: CreateFilamentBody = {
    brand: f.brand.trim(),
    material: f.material,
    name: f.name.trim(),
    colorName: f.colorName.trim() || null,
    colorHex: /^#[0-9A-Fa-f]{6}$/.test(f.colorHex) ? f.colorHex : null,
    pricePerKgCentavos: parseCentavos(f.price) ?? 0,
    minimumGrams: f.minimum.trim() === '' ? null : int(f.minimum),
  };
  if (!full) return base;
  return {
    ...base,
    diameterMm: parseDecimal(f.diameter) ?? 1.75,
    density: parseDecimal(f.density) ?? MATERIAL_DEFAULTS[f.material].density,
    netWeightGrams: int(f.netWeight) ?? 1000,
    emptySpoolGrams: f.emptySpool.trim() === '' ? null : int(f.emptySpool),
    nozzleTempC: int(f.nozzle),
    bedTempC: int(f.bed),
    printNotes: f.notes.trim() || null,
    supplierLinks: f.links
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean),
  };
}

function FilamentForm({
  form,
  setForm,
  full,
  touched,
}: {
  form: Form;
  setForm: (f: Form) => void;
  full: boolean;
  touched: boolean;
}) {
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm({ ...form, [k]: v });
  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(min(160px,100%),1fr))',
    gap: 12,
  } as const;
  const nameBad = touched && form.name.trim() === '';
  // A color outside the quick picks (chosen with "Outra cor", or saved that way).
  const customColor =
    /^#[0-9A-Fa-f]{6}$/.test(form.colorHex) &&
    !FILAMENT_COLORS.some((c) => sameHex(c.hex, form.colorHex));
  const priceBad = touched && parseCentavos(form.price) === null;
  return (
    <>
      <div style={grid}>
        <Field id="f-brand" label="Marca">
          <input
            id="f-brand"
            autoComplete="off"
            className="input"
            value={form.brand}
            onChange={(e) => set('brand', e.target.value)}
          />
        </Field>
        <Field id="f-mat" label="Material">
          <select
            id="f-mat"
            className="input"
            value={form.material}
            onChange={(e) => {
              const m = Material.options.find((x) => x === e.target.value) ?? 'PLA';
              const d = MATERIAL_DEFAULTS[m];
              setForm({
                ...form,
                material: m,
                density: decimalInput(d.density, 2, 2),
                nozzle: String(d.nozzleTempC),
                bed: String(d.bedTempC),
              });
            }}
          >
            {Material.options.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field id="f-name" label="Nome" error={nameBad ? 'Dê um nome ao filamento.' : null}>
        <input
          id="f-name"
          autoComplete="off"
          className={nameBad ? 'input err' : 'input'}
          placeholder="Ex.: PLA Matte Verde"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </Field>
      <Field label="Cor">
        <div className="row wrap" style={{ gap: 8 }}>
          {FILAMENT_COLORS.map((c) => (
            <button
              key={c.hex}
              type="button"
              title={c.name}
              aria-label={c.name}
              aria-pressed={sameHex(form.colorHex, c.hex)}
              onClick={() => setForm({ ...form, colorHex: c.hex, colorName: c.name })}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: c.hex,
                border: sameHex(form.colorHex, c.hex)
                  ? '3px solid var(--pe)'
                  : '1px solid var(--line)',
                cursor: 'pointer',
              }}
            />
          ))}
          <label
            title="Outra cor"
            style={{
              position: 'relative',
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              background: customColor
                ? form.colorHex
                : 'conic-gradient(#FF5C5C, #E8C547, #2E7D4F, #2F6FB5, #6A3FA0, #FF5C5C)',
              border: customColor ? '3px solid var(--pe)' : '1px solid var(--line)',
            }}
          >
            {customColor ? null : (
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--bg)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Icon name="plus" size={12} strokeWidth={3} />
              </span>
            )}
            <input
              type="color"
              aria-label="Outra cor"
              value={
                /^#[0-9A-Fa-f]{6}$/.test(form.colorHex) ? form.colorHex.toLowerCase() : '#000000'
              }
              onChange={(e) => {
                const hex = e.target.value.toUpperCase();
                // A palette name no longer fits a color picked by hand: leave it for the user to type.
                const paletteName = FILAMENT_COLORS.some((c) => c.name === form.colorName);
                setForm({ ...form, colorHex: hex, colorName: paletteName ? '' : form.colorName });
              }}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
          </label>
          <input
            className="input"
            style={{ width: 150, height: 36 }}
            aria-label="Nome da cor"
            placeholder="Ex.: Bronze"
            value={form.colorName}
            onChange={(e) => set('colorName', e.target.value)}
          />
        </div>
      </Field>
      <div style={grid}>
        <Field
          id="f-price"
          label="Preço de reposição"
          error={priceBad ? 'Informe o preço por kg.' : null}
          help="Com frete, dividido pelo peso."
        >
          <AffixInput
            id="f-price"
            prefix="R$"
            unit="/kg"
            value={form.price}
            onChange={(v) => set('price', v)}
            invalid={priceBad}
          />
        </Field>
        <Field id="f-min" label="Mínimo em estoque" help="Abaixo disso entra na lista de compras.">
          <AffixInput
            id="f-min"
            unit="g"
            inputMode="numeric"
            value={form.minimum}
            onChange={(v) => set('minimum', v)}
          />
        </Field>
      </div>
      {full ? (
        <>
          <div style={grid}>
            <Field id="f-dia" label="Diâmetro">
              <AffixInput
                id="f-dia"
                unit="mm"
                value={form.diameter}
                onChange={(v) => set('diameter', v)}
              />
            </Field>
            <Field id="f-den" label="Densidade">
              <AffixInput
                id="f-den"
                unit="g/cm³"
                value={form.density}
                onChange={(v) => set('density', v)}
              />
            </Field>
            <Field id="f-net" label="Peso nominal">
              <AffixInput
                id="f-net"
                unit="g"
                inputMode="numeric"
                value={form.netWeight}
                onChange={(v) => set('netWeight', v)}
              />
            </Field>
            <Field id="f-empty" label="Carretel vazio" help="Para a pesagem.">
              <AffixInput
                id="f-empty"
                unit="g"
                inputMode="numeric"
                value={form.emptySpool}
                onChange={(v) => set('emptySpool', v)}
              />
            </Field>
            <Field id="f-noz" label="Bico">
              <AffixInput
                id="f-noz"
                unit="°C"
                inputMode="numeric"
                value={form.nozzle}
                onChange={(v) => set('nozzle', v)}
              />
            </Field>
            <Field id="f-bed" label="Mesa">
              <AffixInput
                id="f-bed"
                unit="°C"
                inputMode="numeric"
                value={form.bed}
                onChange={(v) => set('bed', v)}
              />
            </Field>
          </div>
          <Field id="f-notes" label="Notas de impressão" help="Temperaturas, secagem, manias.">
            <textarea
              id="f-notes"
              className="input"
              style={{ height: 90, padding: 12, resize: 'vertical' }}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </Field>
          <Field id="f-links" label="Links de fornecedor" help="Um por linha.">
            <textarea
              id="f-links"
              className="input"
              style={{ height: 70, padding: 12, resize: 'vertical' }}
              value={form.links}
              onChange={(e) => set('links', e.target.value)}
            />
          </Field>
        </>
      ) : (
        <p className="xs muted">
          Densidade e temperaturas vêm do padrão do material (
          {decimalInput(MATERIAL_DEFAULTS[form.material].density, 2, 2)} g/cm³); dá para ajustar
          depois.
        </p>
      )}
    </>
  );
}

function NewFilamentDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (f: Filament) => void;
}) {
  const [form, setForm] = useState(toForm());
  const [touched, setTouched] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const create = useMutation({
    mutationFn: () =>
      api.post('/filaments', Filament, toBody(form, false), { idempotencyKey: idem }),
    onSuccess: onCreated,
  });
  return (
    <Modal
      title="Novo filamento"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={create.isPending}
            onClick={() => {
              setTouched(true);
              if (form.name.trim() !== '' && parseCentavos(form.price) !== null) create.mutate();
            }}
          >
            Salvar filamento
          </button>
        </>
      }
    >
      <FilamentForm form={form} setForm={setForm} full={false} touched={touched} />
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
    </Modal>
  );
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "Preço por kg · com frete": one bar per purchase or price change (last 8). */
function PriceHistory({ filamentId }: { filamentId: string }) {
  const prices = useFilamentPrices(filamentId);
  const data = (prices.data?.data ?? []).slice(-8);
  if (data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.pricePerKgCentavos));
  const last = data[data.length - 1]!;
  return (
    <div className="card sunk col" style={{ gap: 8, padding: 12, borderRadius: 12 }}>
      <span className="lbl">Preço por kg · com frete</span>
      <div className="row" style={{ gap: 6, alignItems: 'flex-end', height: 80 }}>
        {data.map((d, i) => (
          <div
            key={`${d.effectiveOn}-${i}`}
            className="col"
            style={{ flex: 1, gap: 4, alignItems: 'center' }}
            title={`${formatBRL(d.pricePerKgCentavos)} em ${d.effectiveOn.split('-').toReversed().join('/')}`}
          >
            <span
              style={{
                width: '100%',
                maxWidth: 28,
                height: Math.max(6, (d.pricePerKgCentavos / max) * 60),
                background: i === data.length - 1 ? 'var(--pe)' : 'var(--line)',
                borderRadius: 4,
              }}
            />
            <span className="xs muted">{MONTHS[Number(d.effectiveOn.slice(5, 7)) - 1]}</span>
          </div>
        ))}
      </div>
      <p className="xs muted">
        Último: <b>{formatBRL(last.pricePerKgCentavos)}/kg</b> em{' '}
        {last.effectiveOn.split('-').toReversed().join('/')}
        {last.supplier ? ` · ${last.supplier}` : ''}
      </p>
    </div>
  );
}

function FilamentSpools({ filamentId }: { filamentId: string }) {
  const spools = useSpools({ filamentId, active: true });
  const rows = spools.data?.data ?? [];
  return (
    <div className="col" style={{ gap: 6 }}>
      <span className="lbl">Carretéis</span>
      {rows.map((s) => (
        <Link
          key={s.id}
          to={`/carreteis/${s.id}`}
          className="row between"
          style={{ gap: 8, padding: '6px 0' }}
        >
          <span className="row" style={{ gap: 8 }}>
            <b className="tab">{s.code}</b>
            <SpoolBadge spool={s} />
          </span>
          <GramsBar spool={s} width={60} />
        </Link>
      ))}
      {spools.isSuccess && rows.length === 0 ? (
        <p className="sm muted">
          Nenhum carretel com filamento. <Link to="/filamentos/compra">Registrar compra</Link>
        </p>
      ) : null}
    </div>
  );
}

function FilamentDrawer({ filament, onClose }: { filament: Filament; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(toForm(filament));
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: () => api.patch(`/filaments/${filament.id}`, Filament, toBody(form, true)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast('Filamento salvo');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/filaments/${filament.id}`, OkResponse),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast('Filamento excluído. Fica na lixeira por 30 dias.');
      onClose();
    },
  });
  return (
    <Drawer
      kicker={`${filament.brand} · ${filament.material}`}
      title={filament.name}
      onClose={onClose}
    >
      <div className="row wrap" style={{ gap: 8 }}>
        <span className={filament.low ? 'badge warn' : 'badge ok'}>
          {formatG(filament.stockGrams)} em estoque
        </span>
        <span className="badge">
          {filament.spoolCount} {filament.spoolCount === 1 ? 'carretel' : 'carretéis'}
        </span>
      </div>
      <FilamentForm form={form} setForm={setForm} full touched={touched} />
      <PriceHistory filamentId={filament.id} />
      <FilamentSpools filamentId={filament.id} />
      {save.isError || remove.isError ? (
        <Alert tone="err">{errorMessage(save.error ?? remove.error)}</Alert>
      ) : null}
      <div className="row wrap" style={{ gap: 10, justifyContent: 'space-between' }}>
        <button
          type="button"
          className="btn g"
          style={{ color: 'var(--err)' }}
          onClick={() => remove.mutate()}
        >
          Excluir
        </button>
        <button
          type="button"
          className="btn p"
          disabled={save.isPending}
          onClick={() => {
            setTouched(true);
            if (form.name.trim() !== '' && parseCentavos(form.price) !== null) save.mutate();
          }}
        >
          Salvar
        </button>
      </div>
      <p className="xs muted">
        Um preço novo vale para orçamentos novos; os já salvos guardam o preço da época.
      </p>
    </Drawer>
  );
}

function Kpis() {
  const summary = useInventorySummary();
  const s = summary.data;
  if (!s) return null;
  return (
    <div
      style={{
        display: 'grid',
        gap: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
        marginBottom: 14,
      }}
    >
      <div className="card col" style={{ gap: 4 }}>
        <span className="lbl">Valor em estoque</span>
        <span className="fr tab" style={{ fontSize: 24 }}>
          {formatBRL(s.valueCentavos)}
        </span>
        <span className="xs muted">pelo preço pago, proporcional ao que sobrou</span>
      </div>
      <div className="card col" style={{ gap: 4 }}>
        <span className="lbl">Filamento disponível</span>
        <span className="fr tab" style={{ fontSize: 24 }}>
          {formatG(s.availableGrams)}
        </span>
        <span className="xs muted">
          em {s.activeSpools} {s.activeSpools === 1 ? 'carretel' : 'carretéis'}
        </span>
      </div>
      <Link to="/filamentos/compras" className="card col" style={{ gap: 4 }}>
        <span className="lbl">Abaixo do mínimo</span>
        <span
          className="fr tab"
          style={{ fontSize: 24, color: s.lowFilaments ? 'var(--warn)' : undefined }}
        >
          {s.lowFilaments}
        </span>
        <span className="xs muted">Ver lista de compras</span>
      </Link>
    </div>
  );
}

/** Estoque · Filamentos e carretéis (boards Filamentos, Filamentos-Carreteis, Filamentos-Compras). */
export function FilamentsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: key,
    queryFn: () => api.get('/filaments?limit=200', FilamentList),
  });
  const params = useParams();
  const tab = params.tab === 'carreteis' || params.tab === 'compras' ? params.tab : 'catalogo';
  const summary = useInventorySummary();
  const [material, setMaterial] = useState<Material | null>(null);
  const [lowOnly, setLowOnly] = useState(false);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const all = list.data?.data ?? [];
  const counts = new Map<Material, number>();
  for (const f of all) counts.set(f.material, (counts.get(f.material) ?? 0) + 1);
  const needle = q.trim().toLowerCase();
  const shown = all.filter(
    (f) =>
      (!material || f.material === material) &&
      (!lowOnly || f.low) &&
      (!needle ||
        [f.name, f.brand, f.colorName ?? ''].some((t) => t.toLowerCase().includes(needle))),
  );
  const open = all.find((f) => f.id === openId) ?? null;
  const cols = '2.2fr 0.9fr 0.9fr 1.3fr 0.9fr 70px';

  return (
    <>
      <PageHead
        crumb="Estoque"
        title="Filamentos e carretéis"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <ExportCsvButton dataset={tab === 'carreteis' ? 'carreteis' : 'filamentos'} />
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              <Icon name="plus" size={18} strokeWidth={2.4} />
              Filamento
            </button>
            <Link className="btn p" to="/filamentos/compra">
              Registrar compra
            </Link>
          </div>
        }
      />
      <Kpis />
      <nav className="tabs" aria-label="Seções">
        <Link to="/filamentos" className={tab === 'catalogo' ? 'on' : ''}>
          Catálogo
        </Link>
        <Link to="/filamentos/carreteis" className={tab === 'carreteis' ? 'on' : ''}>
          Carretéis{summary.data ? ` · ${summary.data.activeSpools}` : ''}
        </Link>
        <Link to="/filamentos/compras" className={tab === 'compras' ? 'on' : ''}>
          Lista de compras{summary.data?.lowFilaments ? ` · ${summary.data.lowFilaments}` : ''}
        </Link>
      </nav>
      {tab === 'carreteis' ? <SpoolsTab /> : null}
      {tab === 'compras' ? <ShoppingTab /> : null}
      {tab !== 'catalogo' ? null : (
        <>
          {list.isPending ? <Skeleton rows={4} height={48} /> : null}
          {list.isError ? (
            <ErrorBox
              title="Não foi possível carregar os filamentos"
              onRetry={() => void list.refetch()}
            />
          ) : null}
          {list.isSuccess && all.length === 0 ? (
            <section className="card">
              <EmptyState
                title="Nenhum filamento ainda"
                action={
                  <button type="button" className="btn p" onClick={() => setCreating(true)}>
                    + Filamento
                  </button>
                }
              >
                Cadastre os filamentos que você usa para o custo e o estoque saírem certos.
              </EmptyState>
            </section>
          ) : null}
          {all.length > 0 ? (
            <section className="card flat">
              <div className="row wrap" style={{ gap: 8, padding: '14px 16px' }}>
                <button
                  type="button"
                  className={material === null ? 'chip on' : 'chip'}
                  onClick={() => setMaterial(null)}
                >
                  Todos · {all.length}
                </button>
                {[...counts.entries()].map(([m, n]) => (
                  <button
                    key={m}
                    type="button"
                    className={material === m ? 'chip on' : 'chip'}
                    onClick={() => setMaterial(m)}
                  >
                    {m} · {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="check sm"
                  role="switch"
                  aria-checked={lowOnly}
                  onClick={() => setLowOnly(!lowOnly)}
                  style={{ marginLeft: 6 }}
                >
                  <span className={lowOnly ? 'toggle on' : 'toggle'} aria-hidden="true" />
                  Só abaixo do mínimo
                </button>
                <div className="grow" />
                <label className="search" style={{ flex: '0 1 260px', height: 38 }}>
                  <Icon name="search" size={16} />
                  <input
                    aria-label="Buscar filamento"
                    placeholder="Buscar"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </label>
              </div>
              <div className="gt">
                <div className="gtr head stack-sm" style={{ gridTemplateColumns: cols }}>
                  <span>Filamento</span>
                  <span>Material</span>
                  <span className="num">Preço/kg</span>
                  <span className="num">Em estoque</span>
                  <span className="num">Mínimo</span>
                  <span />
                </div>
                {shown.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="gtr stack-sm"
                    style={{ gridTemplateColumns: cols }}
                    onClick={() => setOpenId(f.id)}
                  >
                    <span className="row" style={{ gap: 10, minWidth: 0 }}>
                      <span
                        className="dot"
                        style={{
                          background: f.colorHex ?? 'var(--line)',
                          width: 18,
                          height: 18,
                          flexBasis: 18,
                          border: '1px solid var(--line)',
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span className="trunc" style={{ fontWeight: 500 }}>
                          {f.name}
                        </span>
                        <span className="xs muted">
                          {f.brand} · {decimalInput(f.diameterMm, 2, 2)} mm
                        </span>
                      </span>
                    </span>
                    <span>{f.material}</span>
                    <span className="num">{formatBRL(f.pricePerKgCentavos)}</span>
                    <span className="num">
                      <span style={{ display: 'block' }}>{formatG(f.stockGrams)}</span>
                      <span className="xs muted">
                        {f.spoolCount} {f.spoolCount === 1 ? 'carretel' : 'carretéis'}
                      </span>
                    </span>
                    <span className="num">
                      {f.minimumGrams ? `${f.minimumGrams.toLocaleString('pt-BR')} g` : '—'}
                    </span>
                    <span>{f.low ? <span className="badge warn">Baixo</span> : null}</span>
                  </button>
                ))}
                {shown.length === 0 ? (
                  <div className="empty" style={{ padding: 24 }}>
                    <p>Nenhum filamento com esses filtros.</p>
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => {
                        setMaterial(null);
                        setLowOnly(false);
                        setQ('');
                      }}
                    >
                      Limpar filtros
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}
        </>
      )}
      {creating ? (
        <NewFilamentDialog
          onClose={() => setCreating(false)}
          onCreated={(f) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: key });
            toast(`${f.name} cadastrado`);
          }}
        />
      ) : null}
      {open ? (
        <FilamentDrawer key={open.id} filament={open} onClose={() => setOpenId(null)} />
      ) : null}
    </>
  );
}
